import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MicrosoftOutlookLogoIcon as MicrosoftOutlookLogo, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

export default function LoginPage() {
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [needTotp, setNeedTotp] = useState(false);
  const [totp, setTotp] = useState("");

  useEffect(() => {
    api.get("/auth/sso/config").then(({ data }) => setSsoEnabled(!!data.enabled)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get("sso_token");
    const ssoError = params.get("sso_error");
    if (ssoError) {
      setError(decodeURIComponent(ssoError));
      window.history.replaceState({}, "", "/login");
    }
    if (ssoToken) {
      localStorage.setItem("token", ssoToken);
      window.history.replaceState({}, "", "/login");
      api.get("/auth/me").then(({ data }) => { setUser(data); navigate(data.role === "client" ? "/portale" : "/"); })
        .catch(() => { localStorage.removeItem("token"); setError("Sessione SSO non valida"); });
    }
  }, [navigate, setUser]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await login(email, password, needTotp ? totp : null);
      if (data.must_setup_2fa) { navigate("/setup-2fa"); return; }
      navigate(data.user.role === "client" ? "/portale" : "/");
    } catch (err) {
      if (err.response?.headers?.["x-2fa"] === "required") {
        setNeedTotp(true);
        setError(needTotp ? (formatApiErrorDetail(err.response?.data?.detail) || "Codice non valido") : "");
        if (!needTotp) toast.info("Inserisci il codice a 6 cifre della tua app di autenticazione");
      } else {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSso = () => {
    if (!ssoEnabled) {
      toast.info("Microsoft 365 SSO non ancora attivo", {
        description: "Configura l'app registration Azure (Tenant ID, Client ID, Secret) in Impostazioni → Login SSO Microsoft 365.",
      });
      return;
    }
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/sso/login`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center mb-8">
          <img src="/brand/logo.png" alt="Tecnologie" className="h-12 w-auto object-contain mb-4" />
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            Area clienti
          </p>
        </div>

        <div>
          <button
            data-testid="sso-button"
            onClick={handleSso}
            className="w-full flex items-center justify-center gap-3 border border-border bg-white py-2.5 text-sm font-medium hover:bg-secondary transition-colors rounded-sm mb-5"
          >
            <MicrosoftOutlookLogo size={18} weight="fill" className="text-primary" />
            Accedi con Microsoft 365
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">oppure</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@azienda.it"
                className="mt-1.5 rounded-sm"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider">
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 rounded-sm"
                required
              />
            </div>
            {needTotp && (
              <div data-testid="login-totp-block">
                <Label htmlFor="totp" className="text-xs uppercase tracking-wider">Codice di verifica (2FA)</Label>
                <Input
                  id="totp"
                  data-testid="login-totp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={totp}
                  onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  className="mt-1.5 rounded-sm tracking-[0.5em] text-center"
                  autoFocus
                  required
                />
                <p className="text-[11px] text-muted-foreground mt-1">Apri Google Authenticator e inserisci il codice a 6 cifre.</p>
              </div>
            )}
            {error && (
              <p data-testid="login-error" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full rounded-sm bg-primary hover:bg-primary/90 gap-2"
            >
              {loading ? "Accesso..." : "Entra"}
              {!loading && <ArrowRight size={16} weight="bold" />}
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground" data-testid="login-signup-hint">
            Non sei ancora registrato?<br />
            Chiedi al tuo commerciale come funziona.
          </p>

        </div>
      </div>
    </div>
  );
}
